import { banco } from '../db/banco'
import { novoRegistroBase } from '../db/tipos'
import type { DiferencaComissao, ItemPedido, LancamentoComissao, Produto } from '../db/tipos'
import { gravarLocalEEnfileirar, apagarLocalEEnfileirar } from '../sync/outbox'
import { arredondar2, dataISOLocal } from '../pedidos/precos'
import { calcularComissao, escolherRegra, gatilhoEfetivo } from './motor'
import { totalDaBaixa, saldoPendente, saldoDaFornecedora } from './conferencia'
import { saldoDaDiferenca, diferencaDoTotalPago } from './diferencas'

async function itensComProdutos(
  subPedidoId: string,
): Promise<Array<{ item: ItemPedido; produto: Produto }>> {
  const itens = await banco.itens_pedido
    .where('sub_pedido_id')
    .equals(subPedidoId)
    .filter((i) => i.apagado_em === null)
    .toArray()
  const resultado: Array<{ item: ItemPedido; produto: Produto }> = []
  for (const item of itens) {
    const produto = await banco.produtos.get(item.produto_id)
    if (produto) resultado.push({ item, produto })
  }
  return resultado
}

export async function sincronizarLancamento(subPedidoId: string): Promise<void> {
  const subPedido = await banco.sub_pedidos.get(subPedidoId)
  if (!subPedido) return
  const existente = await banco.lancamentos_comissao
    .where('sub_pedido_id')
    .equals(subPedidoId)
    .filter((l) => l.apagado_em === null)
    .first()

  const regras = await banco.regras_comissao
    .where('representada_id')
    .equals(subPedido.representada_id)
    .filter((r) => r.apagado_em === null)
    .toArray()
  const regra = escolherRegra(regras, subPedido.tabela_preco_id)

  const semComissao =
    !regra ||
    subPedido.apagado_em !== null ||
    subPedido.status === 'rascunho' ||
    subPedido.status === 'cancelado'
  if (semComissao) {
    // Dinheiro ja RECEBIDO nunca some da tela. A regra v1 apagava o lancamento
    // "mesmo recebido", e a divida sumia junto com ele: ela recebeu a comissao,
    // a nota foi cancelada depois, e nada na tela dizia que aquilo tinha de
    // voltar para a fabrica (achado dela em 2026-07-30, pedido 2026-0014).
    //
    // Nao ha mais venda, entao o devido cai a zero -- e saldoPendente passa a
    // apontar o valor inteiro como a devolver (negativo).
    if (existente?.status === 'recebida') {
      await gravarLocalEEnfileirar('lancamentos_comissao', {
        ...existente,
        valor_previsto: 0,
        valor_recalculado: null,
      })
      return
    }
    // Sem dinheiro recebido nao ha divida: segue apagando (soft delete, que
    // preserva o historico no servidor para auditoria).
    if (existente) {
      await apagarLocalEEnfileirar('lancamentos_comissao', existente.id)
    }
    return
  }

  const percentuais = await banco.comissao_percentuais
    .where('regra_id')
    .equals(regra.id)
    .toArray()
  const itens = await itensComProdutos(subPedidoId)

  const valorPrevisto = calcularComissao(
    regra, percentuais, itens, subPedido.valor_total, subPedido.valor_total,
  )
  const valorRecalculado =
    subPedido.valor_faturado !== null
      ? calcularComissao(regra, percentuais, itens, subPedido.valor_faturado, subPedido.valor_total)
      : null

  // 'entregue' entra AO LADO de faturado e pago, nao no lugar: sem isso, a
  // comissao ja liberada no faturamento voltaria para "prevista" quando ela
  // confirmasse a entrega -- dinheiro andando para tras sozinho.
  //
  // O gatilho vem da FORNECEDORA, nao da regra da tabela: ver gatilhoEfetivo.
  const gatilho = gatilhoEfetivo(regras, regra)
  const liberada =
    (gatilho === 'faturamento' &&
      (subPedido.status === 'faturado' ||
        subPedido.status === 'entregue' ||
        subPedido.status === 'pago')) ||
    (gatilho === 'pagamento_cliente' && subPedido.status === 'pago')

  // recebida nunca regride
  const status: LancamentoComissao['status'] =
    existente?.status === 'recebida' ? 'recebida' : liberada ? 'liberada' : 'prevista'

  const lancamento: LancamentoComissao = {
    ...(existente ?? { ...novoRegistroBase(), sub_pedido_id: subPedidoId }),
    valor_previsto: valorPrevisto,
    valor_recalculado: valorRecalculado,
    status,
    data_liberacao:
      existente?.data_liberacao ?? (liberada ? dataISOLocal(new Date()) : null),
    data_recebimento: existente?.data_recebimento ?? null,
    valor_recebido: existente?.valor_recebido ?? null,
    // O acerto e decisao dela, nao resultado de calculo: o recalculo nunca o
    // desfaz. Sem estas duas linhas, uma divida ja acertada volta a cobrar.
    valor_acertado: existente?.valor_acertado ?? 0,
    acerto_em: existente?.acerto_em ?? null,
  }
  await gravarLocalEEnfileirar('lancamentos_comissao', lancamento)
}

export async function marcarRecebida(
  lancamento: LancamentoComissao,
  valorRecebido: number,
  dataISO: string,
): Promise<void> {
  await gravarLocalEEnfileirar('lancamentos_comissao', {
    ...lancamento,
    status: 'recebida',
    valor_recebido: valorRecebido,
    data_recebimento: dataISO,
  })
}

// O botao "Pago" da aba Debitos/Creditos: acerta a pendencia INTEIRA. E o
// caminho de quando ela acerta por fora (devolveu por PIX, ou a fabrica pagou o
// que faltava a parte). Acerto parcial digitado a mao ficou fora deste ciclo --
// quando e parcial, acontece pelo desconto do mes, em darBaixaEmLote.
export async function acertarPendencia(
  lancamento: LancamentoComissao,
  dataISO: string,
): Promise<void> {
  const saldo = saldoPendente(lancamento)
  if (saldo === 0) return
  await gravarLocalEEnfileirar('lancamentos_comissao', {
    ...lancamento,
    valor_acertado: arredondar2((lancamento.valor_acertado ?? 0) + saldo),
    acerto_em: dataISO,
  })
}

// A baixa em lote. O valorRecebido de cada linha e o que ELA conferiu: por
// padrao o devido, mas ela pode corrigir na tela quando a fabrica pagou outro
// valor. Nada de rateio automatico -- espalhar uma diferenca por pedidos que
// estavam certos faria nenhum deles bater com a fabrica depois.
export async function darBaixaEmLote(
  selecionados: Array<{ lancamento: LancamentoComissao; valorRecebido: number }>,
  pendentes: LancamentoComissao[],
  dataISO: string,
  // Spec 2026-10-05: sem `opcoes`, exatamente o comportamento de antes.
  opcoes?: {
    diferencas?: DiferencaComissao[]
    totalPago?: { valor: number; mes: string; representadaId: string }
  },
): Promise<void> {
  // A mesma soma que o rodape da tela mostra -- uma funcao so, para o total
  // prometido e o valor gravado nao poderem divergir. As diferencas do mes em
  // aberto entram no saldo como as pendencias dos pedidos.
  const difs = (opcoes?.diferencas ?? []).filter((d) => saldoDaDiferenca(d) !== 0)
  const saldo = arredondar2(saldoDaFornecedora(pendentes) + difs.reduce((s, d) => s + saldoDaDiferenca(d), 0))
  const conta = totalDaBaixa(selecionados.map((s) => s.valorRecebido), saldo)

  // "Numa transacao logica" (spec §4.5). Sao N gravacoes: morrer no meio deixa
  // os selecionados como RECEBIDOS sem o debito ter sido descontado, e na
  // repetida o lote menor limita mais o ajuste -- parte da divida escorrega
  // para o mes seguinte sozinha. gravarLocalEEnfileirar abre transacao nestas
  // mesmas tabelas, entao a de dentro se junta a esta em vez de brigar.
  await banco.transaction('rw', [banco.lancamentos_comissao, banco.diferencas_comissao, banco.outbox], async () => {
    for (const { lancamento, valorRecebido } of selecionados) {
      await marcarRecebida(lancamento, valorRecebido, dataISO)
    }

    // Consome da pendencia mais antiga para a mais nova, so do lado do ajuste --
    // pedidos e diferencas do mes numa fila so, pela data em que nasceram.
    const fila = [
      ...pendentes.map((p) => ({
        quando: p.data_recebimento ?? p.criado_em,
        saldo: saldoPendente(p),
        gravar: (parcela: number) => gravarLocalEEnfileirar('lancamentos_comissao', {
          ...p, valor_acertado: arredondar2((p.valor_acertado ?? 0) + parcela), acerto_em: dataISO,
        }),
      })),
      ...difs.map((d) => ({
        quando: d.data,
        saldo: saldoDaDiferenca(d),
        gravar: (parcela: number) => gravarLocalEEnfileirar('diferencas_comissao', {
          ...d, valor_acertado: arredondar2((d.valor_acertado ?? 0) + parcela), acerto_em: dataISO,
        }),
      })),
    ].sort((a, b) => a.quando.localeCompare(b.quando))
    let restante = conta.ajuste
    for (const item of fila) {
      if (restante === 0) break
      if (item.saldo === 0 || Math.sign(item.saldo) !== Math.sign(restante)) continue
      const parcela = Math.abs(item.saldo) < Math.abs(restante) ? item.saldo : restante
      await item.gravar(parcela)
      restante = arredondar2(restante - parcela)
    }

    // Total pago digitado: a diferenca para o calculado vira debito/credito do
    // mes, DEPOIS do consumo (ela e nova, nao entra no desconto desta baixa).
    const diferenca = diferencaDoTotalPago(conta.total, opcoes?.totalPago?.valor ?? null)
    // Sem nenhum pedido no lote nao ha baixa -- e nao se cria diferenca sozinha
    // (a tela ja desliga o botao; a regra nao pode depender so dela).
    if (opcoes?.totalPago && diferenca !== 0 && selecionados.length > 0) {
      await gravarLocalEEnfileirar('diferencas_comissao', {
        ...novoRegistroBase(), representada_id: opcoes.totalPago.representadaId, mes: opcoes.totalPago.mes,
        valor: diferenca, data: dataISO, valor_acertado: 0, acerto_em: null,
      })
    }
  })
}
